import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { CharacterDTO } from "./CharacterDTO";
import type { CharacterRecord } from "./CharacterRecord";
import { convertIdFilter } from "../../utils/filterUtils";

const findAll = fetcher.path("/api/content/character/findAll").method("post").create();
const getOne = fetcher.path("/api/content/character/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/content/character/{uuid}").method("put").create();
const createOne = fetcher.path("/api/content/character/").method("post").create();
const deleteOne = fetcher.path("/api/content/character/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/content/character/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns CharacterRecord
 */

function mapDTOToRecord(dto: CharacterDTO): CharacterRecord {
    // DTO from backend
    const { id = 0, uuid = "", name = "", description = "" } = dto;

    // Record for front-end
    return {
        shortId: id,
        id: uuid,
        name,
        description
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns CharacterDTO
 */

function mapRecordToDTO(record: CharacterRecord): CharacterDTO {
    // Record from front-end
    const { shortId = 0, id = "", name = "", description = "" } = record;

    // DTO for back-end
    return {
        id: shortId,
        name,
        uuid: id as string,
        description
    };
}

interface UserFilters extends BaseFilters {
    q?: string;
    id: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<CharacterRecord>> {
    const { q: searchTerms, ...filters } = params.filter as UserFilters;

    let parameters: FetchArgType<typeof findAll> = {
        filter: convertIdFilter(filters),
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<CharacterRecord> {
    const {
        data: { content: characterDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(characterDTO);

    return mapDTOToRecord({ id: characterDTO.id ?? 0, ...characterDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<CharacterRecord>> {
    if (!uuids || uuids?.length == 0) {
        return { data: [] };
    }

    const {
        data: { content: characterDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(characterDTO);

    return { data: await Promise.all(characterDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<CharacterRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<CharacterRecord>>): Promise<UpdateResult<CharacterRecord>> {
    const payload = mapRecordToDTO(params.data as CharacterRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const characterDTO = data.content;
    assertIsDefined(characterDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...characterDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<CharacterRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.name);

    const parameters = { name: payload.name, description: payload.description ?? "" };
    const { data } = await createOne(parameters);
    const characterDTO = data.content;

    assertIsDefined(characterDTO);

    return { data: mapDTOToRecord({ id: characterDTO.id as number, ...characterDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<CharacterRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as CharacterRecord;
}

async function deleteMany(params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    await Promise.all(params.ids.map((id: string) => _delete(id)));
    return {
        data: params.ids.map((id: string) => {
            id;
        })
    };
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<CharacterRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const CharacterDataProvider: MpmDataProvider<CharacterRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
